import { Link } from "react-router-dom";
import { Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { imgSrc, money } from "../lib/api";
import { QtyPicker } from "./ProductDetail";

export const ItemOptions = ({ item }) =>
  item.size || item.text || item.addons?.length || item.flavors?.length ? (
    <p className="text-sm text-white/65" data-testid="item-options">
      {item.size && <span>{item.size}</span>}
      {item.size && item.text && " · "}
      {item.text && <span>Spells "<b className="text-white">{item.text.toUpperCase()}</b>"{item.letters ? ` (${item.letters} ${item.letters === 1 ? "letter" : "letters"})` : ""}</span>}
      {item.flavors?.length > 0 && <span className="block" data-testid="item-flavors">Flavors: {item.flavors.join(", ")}</span>}
      {item.addons?.length > 0 && <span className="block text-[var(--pink-soft)]" data-testid="item-addons">+ {item.addons.join(" + ")}</span>}
    </p>
  ) : null;

export default function Cart() {
  const { items, setQty, remove, total } = useCart();

  if (!items.length)
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center" data-testid="cart-empty">
        <ShoppingBag className="mx-auto h-14 w-14 text-[var(--pink)]" />
        <h1 className="mt-6 text-4xl sm:text-5xl">Your cart is empty</h1>
        <p className="mt-3 text-white/60">Something sweet is waiting for you.</p>
        <Link to="/shop" className="btn-candy mt-8" data-testid="cart-empty-shop-button">Browse treats</Link>
      </div>
    );

  return (
    <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6" data-testid="cart-page">
      <h1 className="text-5xl">Your Cart</h1>
      <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_360px]">
        <ul className="space-y-4">
          {items.map((i, idx) => (
            <li key={i.key} className="panel flex flex-wrap items-center gap-4 p-4" data-testid={`cart-item-${i.id}-${idx}`}>
              <img src={imgSrc(i.image_url)} alt={i.name} className="h-20 w-20 rounded-2xl object-cover" />
              <div className="min-w-[140px] flex-1">
                <p className="font-display text-lg leading-tight">{i.name}</p>
                <ItemOptions item={i} />
                <p className="text-[var(--pink)]">{money(i.price)} each</p>
              </div>
              <QtyPicker value={i.quantity} onChange={(v) => setQty(i.key, v)} testid={`cart-qty-${i.id}-${idx}`} />
              <p className="w-20 text-right font-extrabold" data-testid={`cart-line-total-${i.id}-${idx}`}>{money(i.price * i.quantity)}</p>
              <button onClick={() => remove(i.key)} className="rounded-full p-2 text-white/60 hover:bg-white/10 hover:text-[var(--pink)]" data-testid={`cart-remove-${i.id}-${idx}`} aria-label="Remove"><Trash2 className="h-5 w-5" /></button>
            </li>
          ))}
        </ul>
        <aside className="panel h-fit space-y-5 p-6" data-testid="cart-summary">
          <div className="ribbon-thin -mx-6 -mt-6 rounded-t-3xl" />
          <div className="flex justify-between text-lg"><span>Subtotal</span><span className="font-display text-2xl" data-testid="cart-subtotal">{money(total)}</span></div>
          <p className="text-sm text-white/55">Sales tax is calculated at secure checkout.</p>
          <Link to="/checkout" className="btn-candy w-full" data-testid="cart-checkout-button">Checkout</Link>
          <Link to="/shop" className="block text-center text-sm font-bold text-[var(--blue-soft)] hover:text-[var(--pink)]" data-testid="cart-continue-shopping">Continue shopping</Link>
        </aside>
      </div>
    </div>
  );
}
